import React, { useState } from 'react';

interface ValidationIssue {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
  details?: string;
  suggestion?: string;
  valueType?: string;
  code?: string;        // Backend validation error code
  value?: string;       // Original value that caused the issue
  columnIndex?: number; // Index of the column in the CSV
}

export interface ValidationResult {
  rowIndex: number;
  issues: ValidationIssue[];
  fileName?: string;    // For backend validation, includes the source file
  importId?: string;    // ID of the import session
}

// Interface for backend validation summary
export interface ValidationSummary {
  valid_rows: number;
  invalid_rows: number;
  total_rows: number;
  success_rate: number;
  error_count?: number;
  warning_count?: number;
  categories?: Record<string, number>;
}

interface ValidationFeedbackProps {
  validationResults: ValidationResult[];
  maxIssues?: number;
  showAllHandler?: () => void;
  onFixClick?: (fix: { rowIndex: number, field: string, suggestion?: string }) => void;
  validationSummary?: ValidationSummary;
  isProcessing?: boolean;
  serverValidation?: boolean; // Flag to indicate if validation was done on the server
}

const ValidationFeedback: React.FC<ValidationFeedbackProps> = ({
  validationResults,
  maxIssues = 5,
  showAllHandler,
  onFixClick,
  validationSummary,
  isProcessing = false,
  serverValidation = false
}) => {
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  if (!validationResults || validationResults.length === 0) {
    return null;
  }

  // Extract and count issues by severity
  const allIssues = validationResults.flatMap(result => result.issues);

  // Use summary if provided, otherwise calculate counts from results
  const errorCount = validationSummary?.error_count !== undefined
    ? validationSummary.error_count
    : allIssues.filter(issue => issue.severity === 'error').length;

  const warningCount = validationSummary?.warning_count !== undefined
    ? validationSummary.warning_count
    : allIssues.filter(issue => issue.severity === 'warning').length;

  const infoCount = allIssues.filter(issue => issue.severity === 'info').length;

  const totalIssues = validationSummary?.error_count !== undefined && validationSummary?.warning_count !== undefined
    ? validationSummary.error_count + validationSummary.warning_count + infoCount
    : errorCount + warningCount + infoCount;

  // Group issues by field type for better organization
  const issuesByType: Record<string, number> = {};
  allIssues.forEach(issue => {
    if (issue.valueType) {
      issuesByType[issue.valueType] = (issuesByType[issue.valueType] || 0) + 1;
    }
  });

  // Toggle expanded state for a row
  const toggleRowExpansion = (rowIndex: number) => {
    setExpandedRows(prev =>
      prev.includes(rowIndex)
        ? prev.filter(r => r !== rowIndex)
        : [...prev, rowIndex]
    );
  };

  // Sort validation results by severity (errors first) and then by row index
  const sortedResults = [...validationResults].sort((a, b) => {
    const aHasError = a.issues.some(issue => issue.severity === 'error');
    const bHasError = b.issues.some(issue => issue.severity === 'error');

    if (aHasError && !bHasError) return -1;
    if (!aHasError && bHasError) return 1;

    return a.rowIndex - b.rowIndex;
  });

  // Limit displayed issues if needed
  const displayResults = sortedResults.slice(0, maxIssues);
  const hasMoreIssues = sortedResults.length > maxIssues;

  // Helper function to render severity icon
  const renderSeverityIcon = (severity: 'error' | 'warning' | 'info') => {
    switch (severity) {
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
          </svg>
        );
      case 'info':
        return (
          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
          </svg>
        );
    }
  };

  return (
    <div className="rounded-lg border overflow-hidden mb-6">
      <div className="bg-gray-100 p-3 border-b">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-gray-800">
            {serverValidation ? 'Server Validation Results' : 'Client Validation Results'}
            {isProcessing && (
              <span className="ml-2 inline-block h-4 w-4 rounded-full border-2 border-t-blue-600 animate-spin"></span>
            )}
          </h3>
          <div className="flex space-x-2">
            {errorCount > 0 && (
              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full flex items-center">
                <span className="w-2 h-2 rounded-full bg-red-600 mr-1"></span>
                {errorCount} Error{errorCount !== 1 ? 's' : ''}
              </span>
            )}
            {warningCount > 0 && (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full flex items-center">
                <span className="w-2 h-2 rounded-full bg-yellow-600 mr-1"></span>
                {warningCount} Warning{warningCount !== 1 ? 's' : ''}
              </span>
            )}
            {infoCount > 0 && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center">
                <span className="w-2 h-2 rounded-full bg-blue-600 mr-1"></span>
                {infoCount} Info
              </span>
            )}
          </div>
        </div>

        {/* Summary from backend */}
        {validationSummary && (
          <div className="grid grid-cols-4 gap-2 mb-2 mt-1">
            <div className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded flex flex-col items-center">
              <span className="font-semibold">{validationSummary.valid_rows}</span>
              <span>Valid Rows</span>
            </div>
            <div className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded flex flex-col items-center">
              <span className="font-semibold">{validationSummary.invalid_rows}</span>
              <span>Invalid Rows</span>
            </div>
            <div className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded flex flex-col items-center">
              <span className="font-semibold">{validationSummary.total_rows}</span>
              <span>Total Rows</span>
            </div>
            <div className="text-xs px-2 py-1 bg-yellow-50 text-yellow-700 rounded flex flex-col items-center">
              <span className="font-semibold">{validationSummary.success_rate}%</span>
              <span>Success Rate</span>
            </div>
          </div>
        )}

        {/* Issue type breakdown */}
        {Object.keys(issuesByType).length > 0 && (
          <div className="text-xs text-gray-600 flex flex-wrap gap-2 mt-1">
            <span className="font-medium">Issue types:</span>
            {Object.entries(issuesByType).map(([type, count]) => (
              <span key={type} className="px-2 py-0.5 bg-gray-200 rounded-md">
                {type}: {count}
              </span>
            ))}
          </div>
        )}

        {/* Category breakdown from the backend */}
        {validationSummary?.categories && Object.keys(validationSummary.categories).length > 0 && (
          <div className="text-xs text-gray-600 flex flex-wrap gap-2 mt-1 pt-1 border-t border-gray-200">
            <span className="font-medium">Issue categories:</span>
            {Object.entries(validationSummary.categories).map(([category, count]) => (
              <span key={category} className="px-2 py-0.5 bg-gray-200 rounded-md">
                {category.replace('_', ' ')}: {count}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="divide-y">
        {displayResults.map((result, index) => {
          const isExpanded = expandedRows.includes(result.rowIndex);
          const hasErrors = result.issues.some(issue => issue.severity === 'error');
          const hasWarnings = result.issues.some(issue => issue.severity === 'warning');

          return (
            <div
              key={index}
              className={`p-3 bg-white ${hasErrors ? 'bg-red-50' : hasWarnings ? 'bg-yellow-50' : ''}`}
            >
              <button
                onClick={() => toggleRowExpansion(result.rowIndex)}
                className="w-full flex justify-between items-center focus:outline-none"
              >
                <div className="flex items-center">
                  <span className="font-medium text-gray-800">Row {result.rowIndex}</span>
                  {hasErrors && (
                    <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">
                      Critical
                    </span>
                  )}
                </div>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">
                    {result.issues.length} issue{result.issues.length !== 1 ? 's' : ''}
                  </span>
                  <svg
                    className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
                  </svg>
                </div>
              </button>

              {isExpanded && (
                <div className="mt-2">
                  <ul className="space-y-2">
                    {result.issues.map((issue, issueIndex) => (
                      <li
                        key={issueIndex}
                        className={`text-sm p-2.5 rounded ${
                          issue.severity === 'error' ? 'bg-red-50 border border-red-200' :
                          issue.severity === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
                          'bg-blue-50 border border-blue-200'
                        }`}
                      >
                        {/* Add badge for server validation */}
                        {serverValidation && (
                          <div className="flex justify-end mb-1">
                            <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                              Server Validation
                            </span>
                            {issue.code && (
                              <span className="ml-1 text-xs px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
                                Code: {issue.code}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex items-start">
                          <div className="flex-shrink-0 mr-2">
                            {renderSeverityIcon(issue.severity)}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium">
                              {issue.field} {issue.valueType && <span className="text-xs text-gray-500">({issue.valueType})</span>}
                              {issue.columnIndex !== undefined && <span className="text-xs text-gray-500 ml-1">(Column {issue.columnIndex})</span>}
                            </div>
                            <div className="mt-1">{issue.message}</div>

                            {/* Display the original value that caused the issue */}
                            {issue.value !== undefined && (
                              <div className="mt-1 text-xs bg-gray-50 p-1 rounded">
                                <span className="font-medium">Value:</span> {issue.value === '' ? '(empty)' : issue.value}
                              </div>
                            )}

                            {/* Additional details section */}
                            {issue.details && (
                              <div className="mt-1 text-xs text-gray-600">
                                {issue.details}
                              </div>
                            )}

                            {/* Suggestion with fix button */}
                            {issue.suggestion && onFixClick && (
                              <div className="mt-2 flex items-center">
                                <span className="text-xs text-gray-600 mr-2">Suggestion: {issue.suggestion}</span>
                                <button
                                  onClick={() => onFixClick({
                                    rowIndex: result.rowIndex,
                                    field: issue.field,
                                    suggestion: issue.suggestion || ''
                                  })}
                                  className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                >
                                  Apply Fix
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  {/* Row action buttons */}
                  <div className="mt-3 flex justify-end space-x-2">
                    <button className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors">
                      View Row Data
                    </button>
                    {hasErrors && (
                      <button className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100 transition-colors">
                        Skip Row
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hasMoreIssues && (
        <div className="bg-gray-50 p-2 text-center border-t">
          <button
            onClick={showAllHandler}
            className="text-blue-600 text-sm hover:underline flex items-center justify-center mx-auto"
          >
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
            </svg>
            Show All {sortedResults.length} Issues
          </button>
        </div>
      )}

      {/* Summary actions */}
      <div className="bg-gray-50 p-3 border-t flex justify-between items-center">
        <div className="text-xs text-gray-600">
          {errorCount > 0 ? (
            <span className="text-red-600">
              Errors must be resolved before proceeding
            </span>
          ) : warningCount > 0 ? (
            <span className="text-yellow-600">
              Warnings can be ignored but may affect data quality
            </span>
          ) : (
            <span className="text-green-600">
              No critical issues detected
            </span>
          )}
          {serverValidation && validationSummary && (
            <span className="ml-2 px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">
              Server processed: {validationSummary.valid_rows}/{validationSummary.total_rows} rows ({validationSummary.success_rate}%)
            </span>
          )}
        </div>
        <button
          disabled={isProcessing}
          className={`text-sm px-3 py-1.5 ${isProcessing ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'} rounded transition-colors flex items-center`}
        >
          {isProcessing && <span className="mr-1 inline-block h-3 w-3 rounded-full border-2 border-t-blue-600 animate-spin"></span>}
          {serverValidation ? 'Refresh Validation' : 'Validate All Data'}
        </button>
      </div>
    </div>
  );
};

export default ValidationFeedback;